import { jsxs, jsx } from "react/jsx-runtime";
import { useState } from "react";
import classNames from "classnames";
import { useBreakpoint } from "../../hooks/useBreakpoint.js";
import { useHover } from "../../hooks/useHover.js";
import { useUuid } from "../../hooks/useUuid.js";
import { getColor } from "../../theme/currys/color.js";
import { breakpoints } from "../../theme/grid.js";
import { getAriaLabelAttributes } from "../../utils/accessibility.js";
import { N as NotificationBadge } from "../../NotificationBadge.js";
import { C as Close } from "../../Close.js";
import { I as Icon } from "../../Icon.js";
import { IconSize } from "../../constants.js";
import ChevronDown from "../Icons/ChevronDown.js";
import ChevronUp from "../Icons/ChevronUp.js";
import Forward from "../Icons/Forward.js";
import X from "../Icons/X.js";
import styles from "./styles.module.scss";
const Label = ({ label, variant, id, hasExpander, isExpanded, dismissable, onExpand, onDismiss, closeBtnText }) => {
  const breakpoint = useBreakpoint();
  const { isHovered, hoverRef } = useHover();
  const iconSize = breakpoint < breakpoints.lg ? IconSize.XSmall : IconSize.Small;
  const CustomTag = hasExpander ? "button" : "span";
  const labelContainerClasses = classNames(
    styles["service-message__label-container"],
    hasExpander && styles[`service-message__label-container--has-expander`]
  );
  return /* @__PURE__ */ jsx("div", { className: labelContainerClasses, ref: hoverRef, children: /* @__PURE__ */ jsx("div", { className: styles["service-message__container"], children: /* @__PURE__ */ jsx("div", { className: styles["service-message__row"], children: /* @__PURE__ */ jsx("div", { className: styles["service-message__col"], children: /* @__PURE__ */ jsxs("div", { className: styles["service-message__label"], children: [
    /* @__PURE__ */ jsx(NotificationBadge, { variant, size: iconSize, isHovered }),
    /* @__PURE__ */ jsx("h1", { className: styles["service-message__title"], id, children: /* @__PURE__ */ jsx(
      CustomTag,
      {
        className: styles["service-message__toggle"],
        onClick: hasExpander ? onExpand : void 0,
        "aria-expanded": hasExpander ? isExpanded : void 0,
        children: label
      }
    ) }),
    hasExpander && /* @__PURE__ */ jsx(Icon, { size: iconSize, svgIcon: isExpanded ? ChevronUp : ChevronDown, isHovered }),
    !hasExpander && dismissable && /* @__PURE__ */ jsx(Close, { onClick: onDismiss, ariaLabel: closeBtnText, className: styles["service-message__close"] })
  ] }) }) }) }) });
};
const Content = ({ info, extraInfo, urlTitle, url, target, dismissable, closeBtnText, onDismiss }) => {
  const { hoverRef: readMoreRef, isHovered: readMoreHoverRefIsHovered } = useHover();
  const { hoverRef: closeButtonRef, isHovered: closeButtonIsHovered } = useHover();
  const hasUrl = url && urlTitle;
  return /* @__PURE__ */ jsx("div", { className: styles["service-message__container"], children: /* @__PURE__ */ jsx("div", { className: styles["service-message__row"], children: /* @__PURE__ */ jsx("div", { className: styles["service-message__col"], children: /* @__PURE__ */ jsxs("div", { className: styles["service-message__content"], children: [
    info && /* @__PURE__ */ jsx("p", { className: styles["service-message__info"], children: info }),
    extraInfo && /* @__PURE__ */ jsx("p", { className: classNames(styles["service-message__info"], styles["service-message__info--extra"]), children: extraInfo }),
    /* @__PURE__ */ jsxs("div", { className: styles["service-message__actions"], children: [
      hasUrl && /* @__PURE__ */ jsxs("a", { className: styles["service-message__action"], href: url, ref: readMoreRef, target, children: [
        urlTitle,
        /* @__PURE__ */ jsx(Icon, { size: IconSize.XSmall, svgIcon: Forward, color: getColor("blueberry", 700), isHovered: readMoreHoverRefIsHovered })
      ] }),
      dismissable && /* @__PURE__ */ jsxs(
        "button",
        {
          ref: closeButtonRef,
          className: classNames(styles["service-message__action"], styles["service-message__action--close"]),
          "aria-label": closeBtnText,
          onClick: onDismiss,
          children: [
            closeBtnText,
            /* @__PURE__ */ jsx(Icon, { size: IconSize.XSmall, svgIcon: X, color: getColor("blueberry", 700), isHovered: closeButtonIsHovered })
          ]
        }
      )
    ] })
  ] }) }) }) });
};
const ServiceMessage = ({
  label,
  dismissable = true,
  onDismiss,
  info,
  extraInfo,
  urlTitle,
  url,
  target = "_self",
  closeBtnText = "fjern melding",
  expanderOpenFromStart = false,
  variant = "error",
  testId
}) => {
  const [isExpanded, setIsExpanded] = useState(expanderOpenFromStart);
  const labelId = useUuid();
  const hasExpander = !!info || !!extraInfo;
  const ariaRole = variant === "error" ? "alert" : "region";
  const ariaLabelAttributes = getAriaLabelAttributes({ label, id: labelId });
  const handleClick = () => {
    hasExpander && setIsExpanded(!isExpanded);
  };
  const classes = classNames(styles["service-message"], styles[`service-message--${variant}`]);
  return /* @__PURE__ */ jsxs("div", { className: classes, role: ariaRole, ...ariaLabelAttributes, "data-testid": testId, children: [
    /* @__PURE__ */ jsx(
      Label,
      {
        label,
        variant,
        id: labelId,
        hasExpander,
        isExpanded,
        dismissable,
        onExpand: handleClick,
        onDismiss,
        closeBtnText
      }
    ),
    hasExpander && isExpanded && /* @__PURE__ */ jsx(
      Content,
      {
        info,
        extraInfo,
        urlTitle,
        url,
        target,
        dismissable,
        onDismiss,
        closeBtnText
      }
    )
  ] });
};
export {
  ServiceMessage as default
};
//# sourceMappingURL=index.js.map
